import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PendingCorrectionsPanel = ({ 
  pendingCorrections = [], 
  onApprove, 
  onReject,
  onViewDetails 
}) => {
  const [expandedItems, setExpandedItems] = useState(new Set());

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded?.has(id)) {
      newExpanded?.delete(id);
    } else {
      newExpanded?.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    const [h = '00', m = '00'] = timeString.split(':');
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getPriority = (priority) => {
    const priorityMap = {
      high: { color: 'text-error', label: 'HOCH' },
      medium: { color: 'text-warning', label: 'MITTEL' },
      low: { color: 'text-success', label: 'NIEDRIG' },
    };
    return priorityMap[priority] || { color: 'text-text-secondary', label: priority?.toUpperCase() };
  };
  
  const getCategoryIcon = (category) => {
    const categoryConfig = {
      'employee-request': { icon: 'User', color: 'text-primary' },
      'system-error': { icon: 'AlertTriangle', color: 'text-error' },
      'manager-adjustment': { icon: 'Shield', color: 'text-accent' },
      'payroll-correction': { icon: 'DollarSign', color: 'text-success' },
      'technical-issue': { icon: 'Settings', color: 'text-secondary' },
      'policy-exception': { icon: 'FileText', color: 'text-warning' }
    };

    const config = categoryConfig?.[category] || { icon: 'Clock', color: 'text-text-secondary' };
    return <Icon name={config?.icon} size={16} className={config?.color} />;
  };

  if (pendingCorrections?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center">
          <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">Alles erledigt!</h3>
          <p className="text-text-secondary">Keine ausstehenden Korrekturen erfordern Ihre Genehmigung.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Clock" size={20} className="text-warning" />
          <h3 className="text-lg font-semibold text-text-primary">Ausstehende Genehmigungen</h3>
          <div className="bg-warning/10 text-warning px-2 py-1 rounded-full text-xs font-medium">
            {pendingCorrections?.length} Einträge
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpandedItems(new Set())}
          iconName="Minimize2"
          className="text-text-secondary hover:text-text-primary"
        >
          Alle einklappen
        </Button>
      </div>
      {/* Pending Items */}
      <div className="divide-y divide-border">
        {pendingCorrections?.map((correction) => {
          const priorityInfo = getPriority(correction?.priority);
          return (
            <div key={correction?.id} className="p-4">
              {/* Summary Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getCategoryIcon(correction?.category)}
                  <div>
                    <div className="font-medium text-text-primary">
                      {correction?.employee?.name}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {formatDate(correction?.date)} • {correction?.category?.replace('-', ' ')}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <div className={`text-xs font-medium ${priorityInfo.color}`}>
                    {priorityInfo.label}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleExpanded(correction?.id)}
                    iconName={expandedItems?.has(correction?.id) ? "ChevronUp" : "ChevronDown"}
                    className="text-text-secondary hover:text-text-primary"
                  />
                </div>
              </div>

              {/* Expanded Details */}
              {expandedItems?.has(correction?.id) && (
                <div className="mt-4 space-y-4">
                  {/* Time Changes */}
                  <div className="bg-muted/30 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-text-primary mb-2">Angeforderte Änderungen</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-text-secondary">Kommen:</span>
                        <div className="mt-1">
                          <div className="line-through text-text-secondary">
                            {formatTime(correction?.originalClockIn)}
                          </div>
                          <div className="text-primary font-medium">
                            {formatTime(correction?.newClockIn)}
                          </div>
                        </div>
                      </div>
                      <div>
                        <span className="text-text-secondary">Gehen:</span>
                        <div className="mt-1">
                          <div className="line-through text-text-secondary">
                            {formatTime(correction?.originalClockOut)}
                          </div>
                          <div className="text-primary font-medium">
                            {formatTime(correction?.newClockOut)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reason */}
                  <div>
                    <h4 className="text-sm font-medium text-text-primary mb-1">Grund</h4>
                    <p className="text-sm text-text-secondary bg-muted/30 rounded p-2">
                      {correction?.reason}
                    </p>
                  </div>

                  {/* Metadata */}
                  <div className="flex items-center justify-between text-xs text-text-secondary">
                    <div>
                      Angefordert von: {correction?.requestedBy} • {new Date(correction.requestedAt)?.toLocaleString('de-DE')}
                    </div>
                    <div>
                      Eintragsquelle: {correction?.entryType}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-2 pt-2 border-t border-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(correction)}
                      iconName="Eye"
                      iconPosition="left"
                      className="text-text-secondary hover:text-text-primary"
                    >
                      Details anzeigen
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onReject(correction?.id)}
                      iconName="X"
                      iconPosition="left"
                      className="text-error hover:bg-error/10 border-error/20"
                    >
                      Ablehnen
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => onApprove(correction?.id)}
                      iconName="Check"
                      iconPosition="left"
                      className="bg-success hover:bg-success/90 text-success-foreground"
                    >
                      Genehmigen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Bulk Actions Footer */}
      {pendingCorrections?.length > 1 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">
              Massenaktionen für alle ausstehenden Einträge:
            </span>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => pendingCorrections?.forEach(c => onReject(c?.id))}
                iconName="X"
                iconPosition="left"
                className="text-error hover:bg-error/10 border-error/20"
              >
                Alle ablehnen
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => pendingCorrections?.forEach(c => onApprove(c?.id))}
                iconName="CheckCheck"
                iconPosition="left"
                className="bg-success hover:bg-success/90 text-success-foreground"
              >
                Alle genehmigen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingCorrectionsPanel;